import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
// --------------------------------------------------------------------------------
import { ContentWrapper } from './ContentWrapper';
import { String } from './String';
import { config } from '../index';

export interface HorizontalSliderProps {
  /* Slider link props */
  links?: LinkProps[];
  /* Slider margin */
  margin?: string;
  /* Slider padding */
  padding?: string;
  /* Slider width */
  maxWidth?: string;
  /* Slider height */
  minHeight?: string;
  /* Slider content height */
  height?: string;
  /* Slider content width */
  width?: string;
  /* Slider background color*/
  backgroundColor?: string;
  /* Slider border */
  border?: string;
  /* Slider border radius */
  borderRadius?: string;
  /* Slider indicator */
  hasIndicator?: boolean;
  /* Slider component gap */
  gap?: string;
  /* Slider additional className props */
  className?: string;
}

interface LinkProps {
  title?: string;
  url?: string;
}

interface InnerProps extends HorizontalSliderProps {
  progress?: number | string;
}

const ScrollContainer = styled.div<HorizontalSliderProps>`
  position: relative;
  display: flex;
  height: ${(props) => props.height};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  gap: ${(props) => props.gap};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};

  &::-webkit-scrollbar {
    display: none; // 👉 remove scrollbar
  }
`;

const Wrapper = styled.div<HorizontalSliderProps>`
  position: relative;
  border-radius: ${(props) => props.borderRadius};
  overflow: hidden;
`;

const ImgWrapper = styled.div<HorizontalSliderProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  flex: 0 0 auto; // 👉 make it scrollable
  width: ${(props) => props.width};
  scroll-snap-align: start;
  border-radius: ${(props) => props.borderRadius};
`;

const TitleWrapper = styled.div<HorizontalSliderProps>`
  position: absolute;
  top: 3%;
  right: 50%;
  transform: translateX(50%);
  background-color: ${config.transparentMat};
  padding: 0.25rem 1.25rem;
  border-radius: 0.5rem;
  width: max-content;
`;

const SliderProgress = styled.div<InnerProps>`
  position: absolute;
  z-index: 1;
  bottom: 3%;
  right: 3%;
  width: 150px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${config.transparentMat};
  border: 1px solid ${config.primary};
`;

const SliderProgressFill = styled.div<InnerProps>`
  width: ${(props) => props.progress};
  height: 100%;
  border-radius: 2px;
  background-color: ${config.primary};
  transform-origin: left;
  transition: ${config.transitionShort};

  &.active {
    transform: scaleX(1);
  }

  &.inactive {
    transform: scaleX(0);
  }
`;

const GlobalStyle = createGlobalStyle`
  .horizontal-slider-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const HorizontalSlider: React.FC<HorizontalSliderProps> = ({
  /* Input properties */
  links,
  margin,
  padding,
  maxWidth = '1245px',
  minHeight,
  height = '620px',
  width = `calc(1245px * 0.8)`, // 👉 80% of maxWidth
  backgroundColor,
  border = `1px solid ${config.grey}`,
  borderRadius = config.spacingXMedium,
  gap = config.fontXMedium,
  className,
  hasIndicator = true,
  ...props
}) => {
  // --------------------------------------------------------------------------------
  // 📌  From component. Extends Card.tsx
  // --------------------------------------------------------------------------------
  const [progress, setProgress] = useState(0);

  // get scroll percentage of the scroll container  👉 0% ~ 100%
  const getScrollPercentage = () => {
    const scrollLeft = document.querySelector('.horizontal-slider')?.scrollLeft;
    const scrollWidth =
      document.querySelector('.horizontal-slider')?.scrollWidth;
    const clientWidth =
      document.querySelector('.horizontal-slider')?.clientWidth;
    const scrollPercentage =
      (scrollLeft! / (scrollWidth! - clientWidth!)) * 100;

    return Math.round(scrollPercentage); // 👉 round up to the nearest integer
  };

  // 👉 get the index of the current slide
  // const getCurrentSlideIndex = () => {
  //   const scrollPercentage = getScrollPercentage();
  //   const slideIndex = Math.round(scrollPercentage / 100);
  //   return slideIndex;
  // };

  // 👉 get the current slide
  // const getCurrentSlide = () => {
  //   const slideIndex = getCurrentSlideIndex();
  //   const currentSlide = links?.[slideIndex];
  //   return currentSlide;
  // };

  useEffect(() => {
    // add event listener to the scroll container
    const scrollContainer = document.querySelector('.horizontal-slider');
    scrollContainer?.addEventListener('scroll', () => {
      // get percentage of the scroll container
      const scrollPercentage = getScrollPercentage();

      setProgress(scrollPercentage); // 👉 update progress state
    });

    // remove event listener when component unmounts
    return () => {
      scrollContainer?.removeEventListener('scroll', () => {});
    };
  }, []);

  return (
    <ContentWrapper
      {...props}
      margin={margin}
      padding={padding}
      maxWidth={maxWidth}
      minHeight={minHeight}
      backgroundColor={backgroundColor}
      className={className}
    >
      <GlobalStyle />
      <Wrapper borderRadius={borderRadius}>
        {hasIndicator && (
          <SliderProgress>
            <SliderProgressFill progress={progress + '%'} />
          </SliderProgress>
        )}
        <ScrollContainer
          gap={gap}
          height={height}
          className="horizontal-slider"
        >
          {links?.map((link, key) => {
            return (
              <ImgWrapper key={key} borderRadius={borderRadius} width={width}>
                {link?.title && (
                  <TitleWrapper width={width}>
                    <String
                      string={link?.title}
                      color={config.white}
                      fontWeight="bold"
                      fontSize={config.fontXMedium}
                      limit={1} // 👉 limit lines
                    />
                  </TitleWrapper>
                )}

                <img
                  src={link.url}
                  alt={link.title}
                  className="horizontal-slider-img"
                  loading="lazy"
                />
              </ImgWrapper>
            );
          })}
        </ScrollContainer>
      </Wrapper>
    </ContentWrapper>
  );
};
